import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import { formatMinorAmount } from "@/core/utils/func/money-minor";
import type { QrCodeDto } from "@/features/merchant/api/dto";
import {
  QR_STATUSES,
  QR_TYPES,
  type QrStatus,
} from "@/features/shared/api/enums";

export type QrCodeRow = QrCodeDto & { id: string };

const selectOptions = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value }));

const STATUS_VARIANTS: Record<
  QrStatus,
  "success" | "destructive" | "secondary"
> = {
  ACTIVE: "success",
  REVOKED: "destructive",
  EXPIRED: "secondary",
};

/**
 * Colonnes de la liste des QR codes d'un marchand
 * (`GET /QrCodes/merchant/{merchantId}`). Le menu d'actions de ligne est
 * porté par `qr-code-row-actions.tsx` et branché au niveau de la section
 * (accès au merchantId pour l'invalidation).
 */
export const qrCodesColumnsFactory = (
  renderActions: (qr: QrCodeRow) => React.ReactNode
): ColumnsClarFactory<QrCodeRow> =>
  () => [
    {
      id: "payloadToken",
      accessorKey: "payloadToken",
      header: t("merchant.qrCodes.columns.payloadToken"),
      cell: ({ getValue }) => (
        <NoTranslate as="span" className="font-mono text-xs">
          {(getValue() as string) || "—"}
        </NoTranslate>
      ),
      meta: { type: "text" },
    },
    {
      id: "qrType",
      accessorKey: "qrType",
      header: t("merchant.qrCodes.columns.qrType"),
      cell: ({ getValue }) => (
        <NoTranslate as="span">
          <Badge variant="secondary">{(getValue() as string) || "—"}</Badge>
        </NoTranslate>
      ),
      meta: { type: "select", options: selectOptions(QR_TYPES) },
    },
    {
      id: "amount",
      accessorKey: "amountMinor",
      header: t("merchant.qrCodes.columns.amount"),
      cell: ({ row }) => (
        <NoTranslate as="span">
          {row.original.amountMinor != null
            ? formatMinorAmount(
                row.original.amountMinor,
                row.original.currency ?? undefined
              )
            : "—"}
        </NoTranslate>
      ),
      meta: { type: "text" },
    },
    {
      id: "currency",
      accessorKey: "currency",
      header: t("merchant.qrCodes.columns.currency"),
      cell: ({ getValue }) => (
        <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
      ),
      meta: { type: "text" },
    },
    {
      id: "status",
      accessorKey: "status",
      header: t("merchant.qrCodes.columns.status"),
      cell: ({ getValue }) => {
        const status = getValue() as QrStatus | undefined;
        return status ? (
          <NoTranslate as="span">
            <Badge variant={STATUS_VARIANTS[status] ?? "outline"}>
              {status}
            </Badge>
          </NoTranslate>
        ) : (
          "—"
        );
      },
      meta: { type: "select", options: selectOptions(QR_STATUSES) },
    },
    {
      id: "expiresAt",
      accessorKey: "expiresAt",
      header: t("merchant.qrCodes.columns.expiresAt"),
      cell: ({ getValue }) => (
        <NoTranslate as="span">
          {formatDateWithFallback((getValue() as string) ?? "", "—")}
        </NoTranslate>
      ),
      meta: { type: "text" },
    },
    {
      id: "actions",
      header: t("common.actions"),
      enableHiding: false,
      cell: ({ row }) => renderActions(row.original),
      meta: { type: "denied" },
    },
  ];
